import {
  useCallback,
  useEffect,
  useState } from "react";
import { getAllPrenotazioni,
  eliminaPrenotazione,
  aggiornaStatoPrenotazione } from "../APIs/ReservationsAPI";
import { getAllStanze } from "../APIs/RoomsAPI";import {
  Typography,
  Card,
  CardContent,
  Alert,
  Grid,
  Box,
  Button,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Checkbox,
  Chip,
  MenuItem
} from "@mui/material";
import TextField from '../components/SearchableTextField';
import SearchIcon    from "@mui/icons-material/Search";
import DeleteIcon    from "@mui/icons-material/Delete";
import SidebarLayout from "../components/sidebarLayout";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@mui/x-date-pickers";
import { SetupWSEventListener } from "../utils/setupWSEventListener";
import { getTenantTranslation } from "../utils/getTenantTranslation";
import { localizedErrorMessage } from "../utils/italianErrorMessage";

const formatDateTime24 = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("it-IT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

function ListaPrenotazioni() {
  const { t } = useTranslation();
  const tenantType = localStorage.getItem("tenantType");
  const RESERVATION_STATUSES = ["TO_CONFIRM", "CONFIRMED", "COMPLETED", "CANCELLED", "NO_SHOW"];
  const [reservations,  setReservations]  = useState([]);
  const [roomMap,       setRoomMap]       = useState({});
  const [error,         setError]         = useState(null);
  const [dateFilter,    setDateFilter]    = useState(null);
  const [search,        setSearch]        = useState('');
  const [statusFilter,  setStatusFilter]  = useState("ALL");
  const [selectedIds,   setSelectedIds]   = useState(new Set());
  const [confirmOpen,   setConfirmOpen]   = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [pendingCancelReservationId, setPendingCancelReservationId] = useState(null);
  const [cancellationReason, setCancellationReason] = useState("");
  const [cancellationReasonError, setCancellationReasonError] = useState("");
  const [deleting,      setDeleting]      = useState(false);
  const [savingStatusId, setSavingStatusId] = useState(null);

  const getReservationStatusLabel = useCallback((status) => {
    if (!status) return "-";
    return t(`reservations_details.status_values.${status}`, { defaultValue: status });
  }, [t]);

  const getReservationStatusChipSx = useCallback((status) => {
    switch (status) {
      case "TO_CONFIRM":
        return { backgroundColor: "secondary.light", color: "secondary.dark", fontWeight: 700 };
      case "COMPLETED":
        return { backgroundColor: "success.light", color: "success.dark", fontWeight: 700 };
      case "CANCELLED":
        return { backgroundColor: "error.light", color: "error.dark", fontWeight: 700 };
      case "NO_SHOW":
        return { backgroundColor: "warning.light", color: "warning.dark", fontWeight: 700 };
      default:
        return { backgroundColor: "info.light", color: "info.dark", fontWeight: 700 };
    }
  }, []);


  const formatReservationPackages = useCallback((reservation) => {
    const rawPackages = Array.isArray(reservation?.reservationPackages)
      ? reservation.reservationPackages
      : [];
    const packageIds = Array.isArray(reservation?.packageIds) ? reservation.packageIds : [];

    const mappedFromRelations = rawPackages
      .map((entry) => {
        const pkg = entry?.package;
        if (!pkg && Number.isInteger(entry?.packageId)) {
          return {
            id: entry.packageId,
            name: `${t("booking.packageFallbackName")} #${entry.packageId}`,
            priceLabel: null,
          };
        }
        if (!pkg) return null;
        const hasPrice = pkg.price !== null && pkg.price !== undefined && pkg.price !== "";
        return {
          id: pkg.id,
          name: pkg.name || `${t("booking.packageFallbackName")} #${pkg.id}`,
          priceLabel: hasPrice ? `${Number(pkg.price).toFixed(2)} EUR` : null,
        };
      })
      .filter(Boolean);

    if (mappedFromRelations.length > 0) return mappedFromRelations;

    return packageIds
      .filter((id) => Number.isInteger(id) && id > 0)
      .map((id) => ({
        id,
        name: `${t("booking.packageFallbackName")} #${id}`,
        priceLabel: null,
      }));
  }, [t]);

  const formatReservationServices = useCallback((reservation) => {
    const services = Array.isArray(reservation?.eventDay?.services)
      ? reservation.eventDay.services
      : [];

    return services
      .map((entry) => {
        const product = entry?.product;
        if (!product && !entry?.productId) return null;

        const quantity = Number(entry?.quantity ?? 1);
        const unit = product?.pricePerWeight
          ? (product?.typeOfWeight === "KILOGRAM" ? "kg" : product?.typeOfWeight === "HECTOGRAM" ? "hg" : "")
          : "";
        const quantityLabel = quantity !== 1 || unit
          ? ` x${Number.isInteger(quantity) ? quantity : quantity.toFixed(2).replace(/\.?0+$/, "")}${unit}`
          : "";
        const variantLabel = entry?.variant?.name ? ` - ${entry.variant.name}` : "";
        const price = entry?.unitPrice ?? product?.price;

        return {
          id: entry.id ?? entry.productId,
          name: `${product?.name || `Prodotto #${entry.productId}`}${variantLabel}${quantityLabel}`,
          priceLabel: price !== null && price !== undefined && price !== "" ? `${Number(price).toFixed(2)} EUR` : null,
        };
      })
      .filter(Boolean);
  }, [t]);

  const formatReservationCustomRows = useCallback((reservation) => {
    const rows = Array.isArray(reservation?.eventDay?.customRows)
      ? reservation.eventDay.customRows
      : [];

    return rows
      .map((row) => {
        const quantity = Number(row?.quantity ?? 1);
        const price = row?.unitPrice;
        return {
          id: row.id,
          name: `${row.description || "Extra"}${quantity !== 1 ? ` x${quantity}` : ""}`,
          priceLabel: price !== null && price !== undefined && price !== "" ? `${Number(price).toFixed(2)} EUR` : null,
        };
      })
      .filter(Boolean);
  }, []);


  const formatTableReservationsSummary = useCallback((tableReservations = []) => {
    if (!Array.isArray(tableReservations) || tableReservations.length === 0) return "";
    const roomLabel = getTenantTranslation(t, tenantType, "reservationRoom") || t("room");
    const tableLabelKey = getTenantTranslation(t, tenantType, "bookingTable") || t("table");

    const grouped = new Map();
    const fallbackEntries = [];

    tableReservations.forEach((tr) => {
      const name = tr?.table?.name ?? "";
      const cleanName = /^\d+ \d+$/.test(name) ? name.replace(/^\d+ /, "") : name;
      const tableLabel = cleanName ? `${tableLabelKey} ${cleanName}` : tableLabelKey;

      const roomId = tr?.table?.roomId;
      const room = tr?.table?.room ?? roomMap[roomId];
      const roomName = room?.name ?? "";

      if (!roomName) {
        fallbackEntries.push(tableLabel);
        return;
      }

      if (!grouped.has(roomName)) grouped.set(roomName, []);
      grouped.get(roomName).push(tableLabel);
    });

    const roomGroups = Array.from(grouped.entries()).map(
      ([roomName, tableLabels]) => `${roomLabel} ${roomName} - ${tableLabels.join(", ")}`
    );

    return [...roomGroups, ...fallbackEntries].join(", ");
  }, [roomMap, t, tenantType]);

  const getReservationRecipientLabel = useCallback((reservation) => {
    const client = reservation?.client;
    const companyName = client?.company?.companyName || "";
    const name = (client?.name || "").trim();
    const surname = (client?.surname || "").trim();
    const fullName = `${name} ${surname}`.trim();

    if (client) return fullName || companyName || "-";

    const heliteName = (reservation?.heliteHolderName || "").trim();
    const heliteRoom = (reservation?.heliteRoomNumber || "").trim();
    if (heliteName || heliteRoom) {
      return [heliteName, heliteRoom ? `Camera ${heliteRoom}` : null].filter(Boolean).join(" - ");
    }

    return reservation?.eventName?.trim() || "-";
  }, []);

  const getReservationContactLabel = useCallback((reservation) => {
    if (reservation?.client?.email) return reservation.client.email;
    if (reservation?.client?.company?.companyName) return reservation.client.company.companyName;
    if (reservation?.heliteHolderEmail) return reservation.heliteHolderEmail;
    if (reservation?.heliteBillNumber) return `Pren. HElite ${reservation.heliteBillNumber}`;
    if (reservation?.heliteIdrispren) return `HElite ${reservation.heliteIdrispren}`;
    return "";
  }, []);


  const fetchRooms = useCallback(async () => {
    try {
      const token  = localStorage.getItem("accessToken");
      const tenant = localStorage.getItem('tenantId');
      const data   = await getAllStanze(token, tenant);
      if (Array.isArray(data)) {
        const map = {};
        data.forEach(r => { map[r.id] = r; });
        setRoomMap(map);
      }
    } catch (e) {
      console.error('Failed to load rooms:', e);
    }
  }, []);


  const fetchReservations = useCallback(async () => {
    try {
      const token  = localStorage.getItem("accessToken");
      const tenant = localStorage.getItem('tenantId');
      const data   = await getAllPrenotazioni(token, tenant);
      setReservations(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(localizedErrorMessage(err, t("reservations_details.error")));
    }
  }, [t]);


  useEffect(() => {
    fetchRooms();
    fetchReservations();
    const cleanup = SetupWSEventListener(fetchReservations);
    return () => { cleanup(); };
  }, [fetchRooms, fetchReservations]);


  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };


  const handleDeleteConfirmed = async () => {
    setDeleting(true);
    try {
      const token  = localStorage.getItem("accessToken");
      const tenant = localStorage.getItem('tenantId');
      await Promise.all(
        [...selectedIds].map(id => eliminaPrenotazione(id, token, tenant))
      );
      setSelectedIds(new Set());
      setConfirmOpen(false);
      fetchReservations();
    } catch (e) {
      setError(localizedErrorMessage(e, t("reservations_details.error")));
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (reservationId, status, reason) => {
    if (!reservationId || !status) return false;
    const currentReservation = reservations.find((res) => Number(res.id) === Number(reservationId));
    if (currentReservation?.status === "COMPLETED") {
      setError(t("reservations_details.completed_locked", { defaultValue: "Le prenotazioni completate non possono essere modificate." }));
      return false;
    }

    setSavingStatusId(reservationId);
    try {
      const token = localStorage.getItem("accessToken");
      const tenant = localStorage.getItem("tenantId");
      const updated = await aggiornaStatoPrenotazione(reservationId, status, token, tenant, reason);
      setReservations((prev) => prev.map((res) => (
        res.id === reservationId
          ? {
              ...res,
              status: updated?.status ?? status,
              cancellationReason: updated?.cancellationReason ?? null,
            }
          : res
      )));
      return true;
    } catch (e) {
      setError(localizedErrorMessage(e, t("reservations_details.status_update_error")));
      return false;
    } finally {
      setSavingStatusId(null);
    }
  };

  const openCancelStatusDialog = (reservationId) => {
    setPendingCancelReservationId(reservationId);
    setCancellationReason("");
    setCancellationReasonError("");
    setCancelDialogOpen(true);
  };

  const closeCancelStatusDialog = () => {
    setCancelDialogOpen(false);
    setPendingCancelReservationId(null);
    setCancellationReason("");
    setCancellationReasonError("");
  };

  const submitCancelledStatus = async () => {
    const reason = cancellationReason.trim();
    if (!reason) {
      setCancellationReasonError(t("reservations_details.provide_cancellation_reason"));
      return;
    }
    const ok = await handleStatusChange(pendingCancelReservationId, "CANCELLED", reason);
    if (ok) closeCancelStatusDialog();
  };


  const matchesDate = (res) => {
    if (!dateFilter) return true;
    const picked = dateFilter.toDate ? dateFilter.toDate() : new Date(dateFilter);
    const date   = res.startDate ? new Date(res.startDate) : new Date(res.createdAt);
    return (
      date.getFullYear() === picked.getFullYear() &&
      date.getMonth()    === picked.getMonth()    &&
      date.getDate()     === picked.getDate()
    );
  };


  const matchesSearch = (res) => {
    if (!search.trim()) return true;
    const q      = search.toLowerCase();
    const name   = getReservationRecipientLabel(res).toLowerCase();
    const email  = getReservationContactLabel(res).toLowerCase();
    const tables = formatTableReservationsSummary(res.tableReservations).toLowerCase();
    const helite = [
      res.heliteHolderName,
      res.heliteRoomNumber,
      res.heliteBillNumber,
      res.heliteIdrispren,
      res.heliteHolderEmail,
    ].filter(Boolean).join(" ").toLowerCase();
    return name.includes(q) || email.includes(q) || tables.includes(q) || helite.includes(q);
  };

  const matchesStatus = (res) => {
    if (statusFilter === "ALL") return true;
    return (res.status || "TO_CONFIRM") === statusFilter;
  };


  const filtered = reservations
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
    .filter(res => matchesDate(res) && matchesSearch(res) && matchesStatus(res));


  return (
    <SidebarLayout>
      <Box sx={{ mx: { xs: 2, md: 4 }, mt: 2 }}>

        <Typography variant="h4" gutterBottom>
          {t("reservations_details.title")}
        </Typography>

        {/* ── Toolbar ── */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>

          <TextField
            size="small"
            placeholder={t('search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ minWidth: 200, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <DatePicker
            value={dateFilter}
            onChange={value => setDateFilter(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                sx={{ width: 180 }}
                placeholder={t('filter_by_date')}
                inputProps={{ ...params.inputProps, readOnly: true }}
              />
            )}
          />

          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ width: 220 }}
            label={t("reservations_details.status")}
          >
            <MenuItem value="ALL">{t("all")}</MenuItem>
            {RESERVATION_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {getReservationStatusLabel(status)}
              </MenuItem>
            ))}
          </TextField>

          {dateFilter && (
            <Button
              variant="outlined"
              onClick={() => setDateFilter(null)}
              sx={{ minHeight: 44 }}
            >
              {t('clear')}
            </Button>
          )}

          {statusFilter !== "ALL" && (
            <Button
              variant="outlined"
              onClick={() => setStatusFilter("ALL")}
              sx={{ minHeight: 44 }}
            >
              {t('clear')}
            </Button>
          )}

          <Button
            variant="contained"
            color="error"
            disabled={selectedIds.size === 0}
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}
            sx={{ minHeight: 44 }}
          >
            {t('delete')}{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </Button>

          <Button
            variant="contained"
            onClick={() => window.open('/stampa_prenotazioni', '_blank')}
            sx={{ minHeight: 44 }}
          >
            {t('print')}
          </Button>

        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("reservations_details.error")}: {error}
          </Alert>
        )}

        {!error && filtered.length === 0 && (
          <Typography variant="body1" color="text.secondary">
            {reservations.length === 0
              ? t("reservations_details.none")
              : t("reservations_details.no_match")}
          </Typography>
        )}

        {/* ── Cards ── */}
        <Grid container spacing={3}>
          {filtered.map(res => {
            const isSelected         = selectedIds.has(res.id);
            const reservationPackages = formatReservationPackages(res); // ✅ fixed
            const reservationServices = formatReservationServices(res);
            const reservationCustomRows = formatReservationCustomRows(res);
            const recipientLabel = getReservationRecipientLabel(res);
            const contactLabel = getReservationContactLabel(res);

            return (
              <Grid item xs={12} sm={6} lg={4} key={res.id}>
                <Card
                  onClick={() => toggleSelect(res.id)}
                  sx={{
                    height:      '100%',
                    cursor:      'pointer',
                    border:      '2px solid',
                    borderColor: isSelected ? 'error.main' : 'transparent',
                    transition:  'border-color 0.15s, box-shadow 0.15s',
                    boxShadow:   isSelected ? 4 : 1,
                    userSelect:  'none',
                  }}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="h6">
                        {recipientLabel}
                      </Typography>
                      <Checkbox
                        checked={isSelected}
                        color="error"
                        size="small"
                        onClick={e => e.stopPropagation()}
                        onChange={() => toggleSelect(res.id)}
                        sx={{ mt: -0.5, mr: -1 }}
                      />
                    </Box>

                    {contactLabel && (
                      <Typography color="textSecondary" sx={{ mb: 1 }}>
                        {contactLabel}
                      </Typography>
                    )}

                    <Typography sx={{ mt: 1 }}>
                      {formatTableReservationsSummary(res.tableReservations)}
                    </Typography>

                    {reservationPackages.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {t("booking.packagesLabel")}:
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {reservationPackages
                            .map(pkg => `${pkg.name}${pkg.priceLabel ? ` (${pkg.priceLabel})` : ""}`)
                            .join(", ")}
                        </Typography>
                      </Box>
                    )}

                    {reservationServices.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Servizi:
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {reservationServices
                            .map(service => `${service.name}${service.priceLabel ? ` (${service.priceLabel})` : ""}`)
                            .join(", ")}
                        </Typography>
                      </Box>
                    )}

                    {reservationCustomRows.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Extra:
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {reservationCustomRows
                            .map(row => `${row.name}${row.priceLabel ? ` (${row.priceLabel})` : ""}`)
                            .join(", ")}
                        </Typography>
                      </Box>
                    )}

                    <Typography sx={{ mt: 1 }}>
                      {t("reservations_details.date")}:{' '}
                      {formatDateTime24(res.startDate)} — {formatDateTime24(res.endDate)}
                    </Typography>
                    <Box
                      sx={{ mt: 1.25, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {t("reservations_details.status")}:
                      </Typography>
                      <Chip
                        size="small"
                        label={getReservationStatusLabel(res.status)}
                        sx={getReservationStatusChipSx(res.status)}
                      />
                    </Box>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      sx={{ mt: 1.25 }}
                      label={t("reservations_details.change_status")}
                      value={res.status || "TO_CONFIRM"}
                      disabled={savingStatusId === res.id || res.status === "COMPLETED"}
                      helperText={res.status === "COMPLETED" ? t("reservations_details.completed_locked", { defaultValue: "Le prenotazioni completate non possono essere modificate." }) : ""}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const nextStatus = e.target.value;
                        if (nextStatus === "CANCELLED") {
                          openCancelStatusDialog(res.id);
                          return;
                        }
                        handleStatusChange(res.id, nextStatus);
                      }}
                    >
                      {RESERVATION_STATUSES.map((status) => (
                        <MenuItem key={status} value={status}>
                          {getReservationStatusLabel(status)}
                        </MenuItem>
                      ))}
                    </TextField>
                    {res.status === "CANCELLED" && !!res.cancellationReason && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        <strong>{t("reservations_details.cancellation_reason")}:</strong> {res.cancellationReason}
                      </Typography>
                    )}
                    <Typography variant="caption" color="textSecondary">
                      {t("reservations_details.createdAt")}:{' '}
                      {formatDateTime24(res.createdAt)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

      </Box>

      {/* ── Confirm delete dialog ── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{t('confirm_delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('confirm_delete_msg', { count: selectedIds.size })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirmed}
            disabled={deleting}
          >
            {deleting ? '…' : t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={closeCancelStatusDialog} fullWidth maxWidth="sm">
        <DialogTitle>{t("reservations_details.change_status")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("reservations_details.add_cancellation_reason")}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            value={cancellationReason}
            onChange={(e) => {
              setCancellationReason(e.target.value);
              if (cancellationReasonError) setCancellationReasonError("");
            }}
            label={t("reservations_details.cancellation_reason")}
            placeholder={t("reservations_details.cancellation_reason_placeholder")}
            error={Boolean(cancellationReasonError)}
            helperText={cancellationReasonError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelStatusDialog}>{t("cancel")}</Button>
          <Button variant="contained" color="error" onClick={submitCancelledStatus}>
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>

    </SidebarLayout>
  );
}

export default ListaPrenotazioni;
